import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { GroupsEnum } from "../../enum/groups.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { Bank } from "../../model/domain/Bank";
import {
  CounterpartType,
  DialogSize,
  InterestType,
  OperationType,
  OperationTypeQuota,
  Portfolio,
  SettlementType,
  TicketStatus,
  TicketType,
  WorkflowStatus,
} from "../../model/domain/Entity";
import { PrivateFixedIncome } from "../../model/domain/PrivateFixedIncome";
import { StockFixedIncomePosition } from "../../model/domain/StockFixedIncomeModel";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { CounterpartComponent } from "../counterpart/counterpart.component";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";

@Component({
  selector: "app-cetip",
  templateUrl: "./cetip.component.html",
  styleUrls: ["./cetip.component.css"],
})
export class CetipComponent implements OnInit, OnDestroy {
  public model: PrivateFixedIncome = null;
  public groups = GroupsEnum;

  faInfoCircle = faInfoCircle;

  navigationSubscription;

  isEditing = false;
  canValidate = false;
  isSubmit = false;

  decimalPipe = new DecimalPipe("pt");

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("counterpart", { static: true })
  counterpartComponent: CounterpartComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;

  TicketTypeEnum = TicketType;
  WorkFlowStatusEnum = WorkflowStatus;

  dataChanged: EventEmitter<string>;

  TiposCompra: NamedType<number>[];
  ListaTipoJuros: NamedType<number>[];
  ListaBaseIndexador: NamedType<number>[];
  ListaTipoLiquidacao: NamedType<number>[];
  ListaTipoDocumento: NamedType<number>[];
  ListaNoFinancial: NamedType<number>[];
  ListaTipoDoDoAtivo: NamedType<number>[];
  ListaTipoDaContraparte: NamedType<number>[];
  banksList: Bank[] = null;
  hasPropositionIsDisabled = false;
  tempSettlementDate: Date = null;
  isNotProduction = environment.name != "production";

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private authService: AuthService,
    private dialogService: DialogService,
    private snackBarService: SnackbarService,
    private location: Location,
    private loaderService: LoaderService,
    private domainService: DomainService,
    private cutoffService: CutoffService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isSubmit) {
          this.resetForm();
        }
      }
    });
  }

  operationValueIsDisabled: boolean;

  validators = {
    settlementTypeId: (value) =>
      [
        SettlementType.TED,
        SettlementType.CETIP,
        SettlementType.NoFinancial,
      ].includes(value),
    operationTypeId: (value) =>
      [
        OperationType.Redemption,
        OperationType.FullRedemption,
        OperationType.Application,
        OperationType.Deposit,
        OperationType.Withdrawal,
      ].includes(value),
    isSecondaryMarket: (value) =>
      this.isTED() || this.isCETIP() ? [true, false].includes(value) : true,
    operationValue: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.currencyMaskDoubleDecimal, {}),
    indexPercent: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.decimalIndexFeeMask, {}),
    interestType: (value) =>
      this.isSell() || this.isPurchase()
        ? [InterestType.Exponencial, InterestType.Linear].includes(value)
        : true,
    indexBase: (value) =>
      this.isSell() || this.isPurchase() ? [1, 2].includes(value) : true,
    amount: (value) => value != null,
    unitPrice: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    expirationDate: (value) => this.model.operationDate != null && value != "",
    issueDate: (value) =>
      !this.isNoFinancial()
        ? this.model.issueDate != null && value != ""
        : true,
  };

  ngOnInit() {
    this.isSubmit = false;
    this.dataChanged = new EventEmitter<string>();
    this.getTicket();
    this.getBanksList();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new PrivateFixedIncome();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.isEditing = false;
    this.canValidate = false;
    this.model.id = null;
    this.model.command = "0";
    this.model.settlementTypeId = SettlementType.CETIP;

    this.model.operationDate = new Date();
    this.model.isSecondaryMarket = false;
    this.model.assetType = "-1";

    this.isSubmit = false;
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }

  disableActionButtons() {
    let disabled =
      this.model.transferStatusDescription == "Transferência em Trânsito" ||
      this.model.transferStatusDescription == "Transferência Pendente" ||
      this.model.transferStatusDescription == "Transferência Agendada";

    return disabled;
  }

  hasErrorElectronicTransfer() {
    return this.model.transferStatusDescription == "Transferência com Erro";
  }

  setTempId(tempId) {
    this.model.tempTicketId = tempId;
  }

  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.disableSelect();
        this.fundoComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  disableSelect() {
    this.operationValueIsDisabled =
      this.containsGroup([this.groups.Controladoria]) &&
      this.model.statusId != WorkflowStatus.PendingControllershipApproval;
  }

  isAmountRequired() {
    //return this.model.operationTypeId === OperationTypeQuota.FullRedemption;
    let isRequired = this.model.isSecondaryMarket;

    return isRequired;
  }

  getTicket(idBoleta: number = null): void {
    this.model = new PrivateFixedIncome();
    this.model.isSecondaryMarket = false;
    this.model.assetType = "-1";
    this.TiposCompra =
      this.service.ListStandardOperationTypesAndFullRedemption();
    this.ListaBaseIndexador = this.service.ListFoundationIndexes();
    this.ListaTipoJuros = this.service.ListInterestTypes();
    this.ListaTipoLiquidacao = this.service.ListCetipSettlementTypes();
    this.ListaTipoDocumento = this.service.ListTypeOfDocument();
    this.ListaNoFinancial = this.service.ListCetipNoFinancialOperationTypes();
    this.ListaTipoDoDoAtivo = this.service.ListAssetCode();
    this.ListaTipoDaContraparte = this.service.ListCounterparTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.command = "0";

    if (id) {
      this.service.PrivateFixedIncome.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);
          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );

          if (this.model.counterpartDocument) {
            if (this.model.counterpartType === CounterpartType.PessoaJuridica) {
              this.model.counterpartDocument = this.validator.formatDocument(
                this.model.counterpartDocument
              );
            } else if (
              this.model.counterpartType === CounterpartType.PessoaFisica
            ) {
              this.model.counterpartDocument =
                this.validator.formatPhysicalDocument(
                  this.model.counterpartDocument
                );
            }
          }

          this.model.operationDate = new Date(
            this.model.operationDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
          this.model.operationDate.setHours(0, 0, 0, 0);

          if (this.model.acquisitionDate != null) {
            this.model.acquisitionDate = this.datePipe.transform(
              this.model.acquisitionDate.toString().split("T")[0],
              "yyyy-MM-dd",
              "UTC/GMT"
            ) as any;
          }

          if (this.model.expirationDate != null) {
            this.model.expirationDate = this.datePipe.transform(
              this.model.expirationDate.split("T")[0],
              "yyyy-MM-dd",
              "UTC/GMT"
            ) as any;
          }

          if (this.model.settlementDate != null) {
            this.model.settlementDate = this.datePipe.transform(
              this.model.settlementDate.toString().split("T")[0],
              "yyyy-MM-dd",
              "UTC/GMT"
            ) as any;
            this.setSettlementDateBySecondaryMarket(true);
          }

          if (this.model.issueDate != null) {
            this.model.issueDate = this.datePipe.transform(
              this.model.issueDate.toString().split("T")[0],
              "yyyy-MM-dd",
              "UTC/GMT"
            ) as any;
          }

          if (this.model.subscriptionDate != null) {
            this.model.subscriptionDate = this.datePipe.transform(
              this.model.subscriptionDate.toString().split("T")[0],
              "yyyy-MM-dd",
              "UTC/GMT"
            ) as any;
          }

          if (
            this.model.operationTypeId == OperationType.FullRedemption &&
            this.model.statusId == WorkflowStatus.PendingControllershipApproval
          ) {
            this.model.oldOperationValue = this.model.operationValue;
            this.model.operationValue = null;
          }

          if (this.model.amount != null) {
            this.model.amount = this.decimalPipe.transform(
              this.model.amount,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          if (this.model.indexBase != null) {
            this.model.indexBase = +this.decimalPipe.transform(
              this.model.indexBase,
              "1.0-4",
              "pt-BR"
            );
          }

          if (this.model.indexPercent != null) {
            this.model.indexPercent = this.decimalPipe.transform(
              this.model.indexPercent,
              "1.0-4",
              "en-US"
            ) as any;
          }

          if (this.model.indexTax != null) {
            this.model.indexTax = this.decimalPipe.transform(
              this.model.indexTax,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          if (this.model.issueFee != null) {
            this.model.issueFee = this.decimalPipe.transform(
              this.model.issueFee,
              "1.0-4",
              "pt-BR"
            ) as any;
          }

          if (this.model.counterpartType) {
            this.model.counterpartType = +this.decimalPipe.transform(
              this.model.counterpartType,
              "1.0-4",
              "pt-BR"
            );
          }

          this.model.operationValue = this.decimalPipe.transform(
            this.model.operationValue,
            "1.2-8",
            "pt-BR"
          ) as any;
          this.model.unitPrice = this.decimalPipe.transform(
            this.model.unitPrice,
            "1.0-8",
            "pt-BR"
          ) as any;

          this.model.indexPercent = this.decimalPipe.transform(
            this.model.indexPercent,
            "1.2-2",
            "pt-BR"
          ) as any;

          this.fundoComponent.fillComponentFieldsLoad(this.model);

          this.counterpartComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.PrivateFixedIncome])
      );
    } else {
      this.model.operationTypeId = OperationType.Application;
      this.model.settlementTypeId = SettlementType.CETIP;

      this.model.counterpartType = CounterpartType.PessoaJuridica;

      if (!this.model.statusId) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        this.model.operationDate = start;
      }
    }
  }

  submit(): void {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    this.register(this.frm)
      .pipe(
        catchError((err) => {
          this.loaderService.removeLoader();

          this.snackBarService.open(err, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });
          return throwError(err);
        })
      )
      .subscribe(
        (r) => {
          this.loaderService.removeLoader();

          const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

          this.snackBarService.open(msg, "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.isSubmit = false;
        },
        () => {}
      );
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();

    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const counterpartErrors = this.counterpartComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (counterpartErrors.length > 0) {
      return throwError("Verifique os campos nos dados da Contraparte.");
    }

    let data = Object.assign({}, this.model);

    data.operationDate = null;
    data.statusId = null;
    data.id = null;

    data.amount = this.validator.getCurrency(data.amount);
    data.operationValue = this.validator.getCurrency(data.operationValue);
    data.indexPercent = this.validator.getCurrency(data.indexPercent);
    data.unitPrice = this.validator.getCurrency(data.unitPrice);
    data.issueFee = this.validator.getCurrency(data.issueFee);
    data.indexTax = this.validator.getCurrency(data.indexTax);
    data.counterpartDocument = this.validator.sanitize(
      data.counterpartDocument
    );

    if (data.acquisitionDate != null) {
      data.acquisitionDate = new Date(
        data.acquisitionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.subscriptionDate != null) {
      data.subscriptionDate = new Date(
        data.subscriptionDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.expirationDate != null) {
      data.expirationDate = new Date(
        data.expirationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }
    if (data.settlementDate != null) {
      data.settlementDate = this.datePipe.transform(
        data.settlementDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
      this.setSettlementDateBySecondaryMarket(true);
    }
    if (data.issueDate) {
      data.issueDate = new Date(
        data.issueDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.operationDate) {
      data.operationDate = new Date(
        data.operationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (!data.counterpartType) {
      data.counterpartType = CounterpartType.PessoaJuridica;
    }

    if (this.isEscritoTermo()) {
      data.assetCode = null;
    } else {
      data.objectAction = null;
    }

    if (!this.isSell()) {
      data.operationCode = null;
    }

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.counterpartComponent.fillCounterpartData(
      data,
      this.counterpartComponent.counterpartModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (f.invalid) {
      return throwError("Verifique os campos destacados.");
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.PrivateFixedIncome.updateTicket(this.model.id, data)
        : this.service.PrivateFixedIncome.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError("Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((fixedIncome: PrivateFixedIncome) => {
          if (!editing) {
            this.model.id = fixedIncome.id;
            this.model.operationDate = fixedIncome.operationDate;
            this.model.statusId = fixedIncome.statusId;
            this.model.accountManagerName = fixedIncome.accountManagerName;
            this.model.accountManagerDocument =
              fixedIncome.accountManagerDocument;
            this.model.ticketCutoffTypeId = fixedIncome.ticketCutoffTypeId;
            this.model.portfolioFundId = fixedIncome.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  calculateOperationValue(): void {
    const amount = this.validator.getCurrency(this.model.amount);
    const unitPrice = this.validator.getCurrency(this.model.unitPrice);
    const operationValue = amount * unitPrice;
    this.model.operationValue = this.transformDecimal(operationValue);
  }

  isOperationValueModified(): boolean {
    const amount = this.validator.getCurrency(this.model.amount);
    const unitPrice = this.validator.getCurrency(this.model.unitPrice);
    const operationValue = amount * unitPrice;

    return (
      this.validator.getCurrency(this.model.operationValue) !==
      this.validator.getCurrency(this.transformDecimal(operationValue))
    );
  }

  transformDecimal(val: number): number {
    return this.decimalPipe.transform(val, "1.2-8", "pt-BR") as any;
  }

  containsGroup(groups: GroupsEnum[]): boolean {
    return this.authService.hasGroup(groups);
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = "A boleta não poderá mais ser editada, deseja continuar?";

    if (this.isNotProduction) {
      textConfirm +=
        "<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.";
    }

    return this.dialogService
      .confirm("Encaminhar Boleta", textConfirm)
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(err);
          })
        )
        .subscribe(
          (fixedIncome: PrivateFixedIncome) => {
            this.service.PrivateFixedIncome.forwardTicket(
              fixedIncome.id
            ).subscribe((res) => {
              this.model.statusDescription = res.statusDescription;

              this.setEnable(false);

              this.snackBarService.open("Boleta encaminhada.", "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });

              this.getTicket(fixedIncome.id);
            });
          },
          () => {}
        );
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.PrivateFixedIncome.cancelDraftTicket(
              this.model.id
            );
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    if (this.hasErrorElectronicTransfer()) {
      this.dialogService
        .confirm(
          "Aprovar Boleta",
          "ATENÇÃO: O status da Transferência TED/TEF nesta boleta está com erro. Isto significa que a transferência não aconteceu e a liquidação deve ser feita de forma manual. Deseja aprovar a boleta mesmo assim?",
          "Aprovar mesmo assim",
          "Cancelar",
          DialogSize.Medium
        )
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    } else {
      this.dialogService
        .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    }
  }

  private ticketApprove() {
    this.ticketUpdateControllerShip();
    this.service.approve(this.model.id, "").subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");

      this.getTicket(this.model.id);
    });
  }

  private ticketUpdateControllerShip() {
    if (
      this.model.operationTypeId == OperationType.FullRedemption &&
      this.containsGroup([this.groups.Controladoria])
    ) {
      this.model.portfolioDocument = this.model.portfolioDocument.replace(
        /\D/g,
        ""
      );
      this.model.operationValue = this.validator.getCurrency(
        this.model.operationValue
      );
      this.service.PrivateFixedIncome.updateOperationValue(
        this.model.id,
        this.model
      ).subscribe(() => {});
    }
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket(this.model.id);
            });
        }
      });
  }
  getBanksList() {
    this.domainService.ListBanks().subscribe((b) => {
      this.banksList = b;
    });
  }
  coalesce(value: any) {
    return value || "";
  }

  isSell(): boolean {
    return (
      this.model.operationTypeId === OperationType.Redemption ||
      this.model.operationTypeId === OperationType.FullRedemption
    );
  }

  isNoFinancial(): boolean {
    return this.model.settlementTypeId === SettlementType.NoFinancial;
  }

  isTED(): boolean {
    return this.model.settlementTypeId === SettlementType.TED;
  }

  isCETIP(): boolean {
    return this.model.settlementTypeId === SettlementType.CETIP;
  }

  isPurchase(): boolean {
    return this.model.operationTypeId === OperationType.Application;
  }

  isDeposit(): boolean {
    return this.model.operationTypeId === OperationType.Deposit;
  }

  isCetipVoice(): boolean {
    if (this.isCETIP() && this.isPurchase()) {
      switch (this.model.assetType) {
        case "DEB":
        case "CRA":
        case "CRI":
          return true;
        default:
          return false;
      }
    }
  }

  isNaoPadronizados(): boolean {
    if ((this.isCETIP() || this.isTED()) && this.isPurchase()) {
      switch (this.model.assetType) {
        case "DEB":
        case "CRA":
        case "CRI":
        case "CCI":
        case "CCE":
        case "CCB":
        case "CPR":
          return true;
        default:
          return false;
      }
    }
  }

  isWithdrawal(): boolean {
    return this.model.operationTypeId === OperationType.Withdrawal;
  }

  isFisica(): boolean {
    return this.model.counterpartType === +CounterpartType.PessoaFisica;
  }

  isJuridica(): boolean {
    return this.model.counterpartType === +CounterpartType.PessoaJuridica;
  }

  isFullRedemption(): boolean {
    return this.model.operationTypeId === OperationTypeQuota.FullRedemption;
  }

  isRedemption(): boolean {
    return this.model.operationTypeId === OperationTypeQuota.Redemption;
  }

  /**
   * @description Is asset type equals 'termo'
   */
  isEscritoTermo(): boolean {
    return (
      this.model.assetType !== null &&
      this.model.assetType.toString().toLowerCase() === "termo"
    );
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });
    return validationErrors;
  }

  duplicate(): void {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.CETIP },
          ]);
        });
    });
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateTicketPrivateFixedIncome)
    );
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.PrivateFixedIncome;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateTicketPrivateFixedIncome) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateTicketPrivateFixedIncome)
    );
  }

  getStockSelectedFixedIncome(selectedFixedIncome: StockFixedIncomePosition) {
    if (this.isRedemption() || this.isFullRedemption()) {
      this.model.assetType = selectedFixedIncome.code;
      this.model.assetCode = selectedFixedIncome.issueCode;
      this.model.issuer = selectedFixedIncome.issuerName;
      this.model.issueDate = this.datePipe.transform(
        selectedFixedIncome.issueDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
      this.model.expirationDate = this.datePipe.transform(
        selectedFixedIncome.dueDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
      this.model.unitPrice = this.decimalPipe.transform(
        selectedFixedIncome.pu,
        "1.0-8",
        "pt-BR"
      ) as any;

      this.model.operationCode = selectedFixedIncome.operationCode;
      this.model.acquisitionDate = this.datePipe.transform(
        selectedFixedIncome.purchaseDate,
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;

      if (this.isFullRedemption()) {
        this.model.operationValue = this.decimalPipe.transform(
          selectedFixedIncome.netValue,
          "1.0-2",
          "pt-BR"
        ) as any;
        this.model.amount = selectedFixedIncome.quantity;
      }
    }
  }

  getCounterpartType(counterpartType: number) {
    this.model.counterpartType = counterpartType;
  }

  showTransferStatus() {
    return (
      this.model.id != null &&
      this.model.id != 0 &&
      this.model.transferStatusDescription != null &&
      this.model.transferStatusDescription != ""
    );
  }

  setSettlementDateBySecondaryMarket(saveSettlementDate: boolean = false) {
    if (this.model.isSecondaryMarket) {
      this.tempSettlementDate = this.model.settlementDate;
      this.model.settlementDate = this.datePipe.transform(
        new Date(),
        "yyyy-MM-dd",
        "UTC/GMT"
      ) as any;
    } else if (!saveSettlementDate) {
      this.model.settlementDate = this.tempSettlementDate;
    } else {
      this.tempSettlementDate = this.model.settlementDate;
    }
  }
}
